<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import Input from '../../../../../components-next/input/Input.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'exemplo.com.br ou https://exemplo.com.br/faq#secao',
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  maxLength: {
    type: Number,
    default: 100 // Máximo de 100 caracteres para URL
  }
});

const emit = defineEmits(['update:modelValue', 'validation']);

const websiteValue = ref(props.modelValue);
const isValid = ref(!props.required); // Começa válido se não for obrigatório
const isTouched = ref(false);
const internalError = ref('');

const validateWebsite = (website) => {
  if (!website) {
    if (props.required) {
      internalError.value = 'O site da empresa é obrigatório';
      return false;
    }
    // Se não for obrigatório e estiver vazio, é válido
    internalError.value = '';
    return true;
  }

  // Limpar espaços em branco no início e fim
  const cleanedWebsite = website.trim();

  // Não permite espaços no meio da URL
  if (cleanedWebsite.includes(' ')) {
    internalError.value = 'A URL não pode conter espaços';
    return false;
  }

  // Verificar tamanho (máximo de 100 caracteres por padrão)
  if (cleanedWebsite.length > props.maxLength) {
    internalError.value = `O site não pode ter mais de ${props.maxLength} caracteres`;
    return false;
  }

  // Verificar se tem no mínimo 4 caracteres (ex: a.co)
  if (cleanedWebsite.length < 4) {
    internalError.value = 'URL muito curta';
    return false;
  }

  // Verifica caracteres não ASCII (acentos, ç, etc) que não são válidos em URLs
  if (/[^\x00-\x7F]/.test(cleanedWebsite)) {
    internalError.value = 'A URL não pode conter caracteres especiais ou acentos';
    return false;
  }

  // Verifica caracteres especiais inválidos
  const invalidChars = /[<>{}|\\^`\[\]"\s]/;
  if (invalidChars.test(cleanedWebsite)) {
    internalError.value = 'A URL contém caracteres inválidos';
    return false;
  }

  // Extrai o domínio (remove protocolo, path, query, hash)
  let domainPart = cleanedWebsite.replace(/^https?:\/\//, ''); // Remove protocolo
  domainPart = domainPart.replace(/^www\./, ''); // Remove www. opcional
  domainPart = domainPart.split('/')[0]; // Pega só a parte do domínio
  domainPart = domainPart.split(':')[0]; // Remove porta se houver
  domainPart = domainPart.split('?')[0]; // Remove query string se houver
  domainPart = domainPart.split('#')[0]; // Remove hash se houver

  // Verifica se contém pelo menos um ponto
  if (!domainPart.includes('.')) {
    internalError.value = 'O domínio deve conter pelo menos um ponto (.)';
    return false;
  }

  // Verifica se todas as partes do domínio são válidas (não vazias)
  const parts = domainPart.split('.');
  if (parts.some(part => !part || part.length === 0)) {
    internalError.value = 'Domínio inválido';
    return false;
  }

  // Verifica se termina com .alguma_coisa (pelo menos 2 caracteres após o último ponto)
  const lastPart = parts[parts.length - 1];
  if (lastPart.length < 2) {
    internalError.value = 'O domínio deve terminar com uma extensão válida (.com, .br, etc)';
    return false;
  }

  // Valida cada parte do domínio
  const domainPartRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/i;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!domainPartRegex.test(parts[i])) {
      internalError.value = 'Domínio inválido. Use apenas letras, números e hífens';
      return false;
    }
  }

  // Valida a extensão (TLD) - só letras
  const tldRegex = /^[a-z]{2,}$/i;
  if (!tldRegex.test(lastPart)) {
    internalError.value = 'Extensão do domínio inválida. Use apenas letras (.com, .br, etc)';
    return false;
  }

  // Se tem path, valida o path
  const pathMatch = cleanedWebsite.match(/^(https?:\/\/)?(www\.)?[^\/]+(\/.*)?$/);
  if (pathMatch && pathMatch[3]) {
    const path = pathMatch[3];

    // Valida caracteres permitidos no path: letras, números, -, _, ., ~, %, /, ?, #, &, =, +
    const validPathRegex = /^[a-zA-Z0-9\-_.~%\/?#&=+]*$/;
    if (!validPathRegex.test(path)) {
      internalError.value = 'Path da URL contém caracteres inválidos';
      return false;
    }

    // Verifica múltiplas barras consecutivas (exceto após protocolo)
    if (/(?<!:)\/\//.test(path)) {
      internalError.value = 'Path da URL não pode conter barras consecutivas';
      return false;
    }

    // Verifica se o path contém algo que parece ser outro domínio completo
    // Remove query string e hash antes de verificar
    const pathWithoutParams = path.split('?')[0].split('#')[0];

    // Detecta padrões como "/exemplo.com" ou "/subpath/outro.com.br"
    // Permite paths como "/api.json" ou "/file.txt" mas bloqueia "/site.com"
    const suspiciousDomainInPath = /\/[a-z0-9-]+\.(com|br|org|net|gov|edu|mil|int|info|biz|name|museum|coop|aero|xxx|idv|co|me|io|ai|app|dev|tech|online|site|blog|shop|store|cloud|digital|web|xyz|top|pro|academy|agency|solutions|services|company|ventures|capital|consulting|direct|email|guru|network|center|support|systems|world|global|zone)\b/i;

    if (suspiciousDomainInPath.test(pathWithoutParams)) {
      internalError.value = 'URL parece conter outro domínio no path. Verifique se digitou corretamente';
      return false;
    }
  }

  // Regex final para validar formato completo
  const urlRegex = /^(https?:\/\/)?(www\.)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(:[0-9]{1,5})?(\/[a-zA-Z0-9\-_.~%\/?#&=+]*)?$/i;
  if (!urlRegex.test(cleanedWebsite)) {
    internalError.value = 'Formato de URL inválido. Ex: exemplo.com/faq#secao';
    return false;
  }

  internalError.value = '';
  return true;
};

const handleInput = (e) => {
  const value = e.target.value.trim();
  websiteValue.value = value;
  emit('update:modelValue', value);
  
  // Valida em tempo real independente se o campo foi tocado
  validateAndEmit();
};

const validateAndEmit = () => {
  isValid.value = validateWebsite(websiteValue.value);
  emit('validation', isValid.value);
  return isValid.value;
};

const handleBlur = () => {
  // Marca campo como tocado para mostrar erros visuais
  isTouched.value = true;
  validateAndEmit();
};

// Observar mudanças no modelo
watch(() => props.modelValue, (newValue) => {
  if (newValue !== websiteValue.value) {
    websiteValue.value = newValue;
    validateAndEmit();
  }
});

// Validar e emitir no montagem do componente
onMounted(() => {
  // Validar o valor inicial sem mostrar erro
  isValid.value = validateWebsite(websiteValue.value);
  emit('validation', isValid.value);
});

const errorMessage = computed(() => {
  // Só mostrar erro se o campo já foi tocado
  if (!isTouched.value) return '';
  return props.error || internalError.value;
});
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <Input
      :modelValue="websiteValue"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :error="errorMessage"
      @input="handleInput"
      @blur="handleBlur"
      @update:modelValue="emit('update:modelValue', $event)"
    />
    <!-- Altura fixa para mensagem de erro -->
    <div class="h-5 text-n-ruby-9 text-sm">
      <span v-if="errorMessage && isTouched">{{ errorMessage }}</span>
      <span v-else-if="!isTouched && required" class="text-n-slate-10 text-xs">
        Aceita URLs com ou sem protocolo e âncoras (ex: exemplo.com/faq#secao)
      </span>
    </div>
  </div>
</template>